/**
 * The pipeline's changes as one panel (pipelines.md 2.1): what is happening now, grouped by work
 * state, with history one segment away. Now holds what is in flight and what finished in the
 * last few minutes; History every recent result, newest first; Resubmitted the work that was
 * rejected and sent again, one row per piece of work. The view is in the address (`?list=`), so
 * a link or the breadcrumb opens the same one. With nothing in flight, History is the default.
 */
import { Search } from '@orgus/ui/icons';
import { useCallback, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import type { Pipeline, PipelineItem } from '@orgus/protocol';
import { Input, Panel, Segmented, useNow } from '@orgus/ui';
import { Board, nowCards } from './Board.tsx';
import { pipelineChains, resubmitted } from './chains.ts';
import { ResubmittedList } from './History.tsx';
import { RecentList } from './Recent.tsx';

export type ChangesView = 'now' | 'history' | 'resubmitted';

/** The list offers a search from this many changes; fewer read at a glance. */
const SEARCH_FROM = 8;

/** The view the address asks for (`?list=`, or the older `?results=resubmitted`), else null. */
export function askedView(params: URLSearchParams): ChangesView | null {
  const v = params.get('list');
  if (v === 'now' || v === 'history' || v === 'resubmitted') return v;
  return params.get('results') === 'resubmitted' ? 'resubmitted' : null;
}

export function Changes({ pipeline: p, onOpen, activeItemId, extra }: { pipeline: Pipeline; onOpen: (i: PipelineItem) => void; activeItemId?: string | null; extra?: ReactNode }) {
  const [params, setParams] = useSearchParams();
  const now = useNow(5000);
  const nowCount = useMemo(() => nowCards(p, now).length, [p, now]);
  const chains = useMemo(() => resubmitted(pipelineChains(p)).length, [p]);
  const asked = askedView(params);
  const view: ChangesView = asked === 'resubmitted' && !chains ? 'history' : (asked ?? (nowCount ? 'now' : 'history'));
  const [query, setQuery] = useState('');
  const setView = useCallback(
    (v: ChangesView) =>
      setParams(
        (prev) => {
          const n = new URLSearchParams(prev);
          n.delete('results');
          n.set('list', v);
          return n;
        },
        { replace: true, preventScrollReset: true },
      ),
    [setParams],
  );
  const searchable = nowCount + p.recent.length >= SEARCH_FROM || query !== '';
  const options = [
    { value: 'now' as const, label: 'Now', count: nowCount || undefined },
    { value: 'history' as const, label: 'History', count: p.recent.length || undefined },
    ...(chains ? [{ value: 'resubmitted' as const, label: 'Resubmitted', count: chains }] : []),
  ];

  return (
    <Panel flush aria-label="Changes">
      <div className="flex min-h-filter-bar flex-wrap items-center gap-x-4 gap-y-2 px-3 pt-3 pb-1">
        <div className="max-w-full overflow-x-auto">
          <Segmented label="Changes" value={view} onChange={setView} options={options} />
        </div>
        {extra}
        {searchable && (
          <Input
            size="sm"
            icon={<Search />}
            placeholder="Find a change"
            aria-label="Find a change"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            wrapperClassName="ml-auto w-64 max-sm:ml-0 max-sm:w-full"
          />
        )}
      </div>
      <div className="pb-2">
        {view === 'now' ? (
          <div className="pt-2">
            <Board pipeline={p} onOpen={onOpen} activeItemId={activeItemId} filter={{ state: null, query }} />
          </div>
        ) : view === 'history' ? (
          <RecentList pipeline={p} onOpen={onOpen} query={query} />
        ) : (
          <ResubmittedList pipeline={p} onOpen={onOpen} query={query} />
        )}
      </div>
    </Panel>
  );
}
